import set from "lodash/set";
import { useRef, useState } from "react";

import { validateAmountInput } from "./formatters";
import {
  type AgeBasedAmountEntry,
  type DraftBand,
  isValidBand,
  makeDraftBand,
  toDraftBands,
} from "./AgeBandAmountsField";
import { writeCollapsedSiblings } from "./siblingWrites";
import { useCustomTextEditor } from "./useCustomTextEditor";

import type { ICellEditorParams } from "ag-grid-community";
import type { Dispatch, SetStateAction } from "react";
import type { EditMode } from "./formatters";
import type { JsonValue } from "../utils/popupCustomText";

/** A gated scalar input (salary multiplier, dollar/cap, additional amount). */
export interface GuaranteeIssueScalarInput {
  /** Sibling row's benefitKey. */
  key: string;
  label: string;
  /** Type values for which this input is shown. */
  showForTypes: string[];
  /** Initial value from the collapsed sibling: number when saved, else null. */
  initialValue?: number | null;
}

export interface GuaranteeIssueCellEditorParams {
  initialType: string | undefined;
  typeKey: string;
  typeOptions: { value: string; label: string }[];
  scalarInputs: GuaranteeIssueScalarInput[];
  /** Sibling row's benefitKey holding the age-based amounts array. */
  agesDetailsKey: string;
  /** The type value that shows the age-band list (e.g. "ageBasedAmounts"). */
  bandsForType: string;
  initialBands: AgeBasedAmountEntry[];
  initialCustomText?: string | null;
  onCustomTextChange?: (
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
}

export type GuaranteeIssueEditorProps = ICellEditorParams &
  GuaranteeIssueCellEditorParams;

/**
 * Write each sibling row's value in the current column, in one node pass.
 * `triggerKey`'s write goes through `setDataValue`, so all other entries are
 * written silently first — otherwise the triggering PATCH could fire before
 * a sibling entry (e.g. the age bands) has landed on its row.
 */
function persistEntries(
  entries: [string, unknown][],
  triggerKey: string,
  props: Pick<GuaranteeIssueEditorProps, "api" | "colDef" | "column">,
): void {
  const colKey = props.column ?? props.colDef?.field;
  if (!colKey) return;
  const colId = typeof colKey === "string" ? colKey : colKey.getColId();

  props.api.forEachNode((n) => {
    const match = entries.find(([key]) => key === n.data?.benefitKey);
    if (!match) return;
    if (match[0] === triggerKey) return;
    const data = n.data as { benefit?: { networkDependent?: boolean } };
    const path =
      data.benefit?.networkDependent === true || !colId.includes(".")
        ? colId
        : colId.split(".")[0];
    set(n.data, path, match[1]);
  });

  props.api.forEachNode((n) => {
    const match = entries.find(([key]) => key === n.data?.benefitKey);
    if (match && match[0] === triggerKey) n.setDataValue(colKey, match[1]);
  });
}

export interface GuaranteeIssueEditor {
  draftType: string;
  changeType: (value: string) => void;
  draftScalars: Record<string, string>;
  setScalar: (key: string, value: string) => void;
  bands: DraftBand[];
  updateBand: (index: number, field: keyof DraftBand, value: string) => void;
  addBand: () => void;
  removeBand: (index: number) => void;
  errors: Record<string, string>;
  showBandErrors: boolean;
  draftCustomText: string;
  setDraftCustomText: Dispatch<SetStateAction<string>>;
  draftCharacterCount: number;
  setDraftCharacterCount: Dispatch<SetStateAction<number>>;
  editMode: EditMode;
  setEditMode: Dispatch<SetStateAction<EditMode>>;
  showBands: boolean;
  visibleScalars: GuaranteeIssueScalarInput[];
  customTextSupported: boolean;
  editingCustomText: boolean;
  getValue: () => unknown;
  handleSave: () => void;
  handleCancel: () => void;
}

/**
 * State and save/cancel logic for GuaranteeIssueCellEditor, extracted so the
 * component stays a thin render (and each function's complexity stays low).
 */
export function useGuaranteeIssueEditor(
  props: GuaranteeIssueEditorProps,
): GuaranteeIssueEditor {
  const {
    initialType,
    typeKey,
    scalarInputs,
    agesDetailsKey,
    bandsForType,
    initialBands,
    initialCustomText,
    onCustomTextChange,
    api,
    colDef,
    node,
    stopEditing,
  } = props;

  const [draftType, setDraftType] = useState<string>(initialType ?? "");
  const [draftScalars, setDraftScalars] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      scalarInputs.map((input) => [
        input.key,
        input.initialValue != null ? String(input.initialValue) : "",
      ]),
    ),
  );
  const [bands, setBands] = useState<DraftBand[]>(toDraftBands(initialBands));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showBandErrors, setShowBandErrors] = useState(false);
  const customText = useCustomTextEditor(
    initialCustomText,
    onCustomTextChange,
    stopEditing,
  );

  const lastValueRef = useRef<unknown>(initialType ?? null);

  const showBands = draftType === bandsForType;
  const visibleScalars = scalarInputs.filter((input) =>
    input.showForTypes.includes(draftType),
  );

  const changeType = (value: string): void => {
    setDraftType(value);
    setErrors({});
    setShowBandErrors(false);
  };

  const setScalar = (key: string, value: string): void => {
    setDraftScalars((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const updateBand = (
    index: number,
    field: keyof DraftBand,
    value: string,
  ): void =>
    setBands((prev) =>
      prev.map((b, i) => (i === index ? { ...b, [field]: value } : b)),
    );
  const addBand = (): void => setBands((prev) => [...prev, makeDraftBand()]);
  const removeBand = (index: number): void =>
    setBands((prev) => prev.filter((_, i) => i !== index));

  const saveCustomText = (): void => {
    lastValueRef.current = initialType ?? null;
    customText.saveCustomText(initialType);
  };

  const handleSave = (): void => {
    if (customText.editingCustomText) return saveCustomText();

    const nextErrors: Record<string, string> = {};
    for (const input of visibleScalars) {
      nextErrors[input.key] = validateAmountInput(
        draftScalars[input.key] ?? "",
        {
          min: 0,
          integer: false,
        },
      );
    }
    const bandsInvalid = showBands && !bands.every(isValidBand);
    if (bandsInvalid || Object.values(nextErrors).some((e) => e !== "")) {
      setErrors(nextErrors);
      setShowBandErrors(bandsInvalid);
      return;
    }

    // Each scalar persists its parsed value when visible, or null when hidden
    // (so switching type clears stale amounts); bands only for the band type.
    const entries: [string, unknown][] = [
      [typeKey, draftType || null],
      [
        agesDetailsKey,
        showBands
          ? bands.map((b) => ({
              amount: Number(b.amount),
              ageLower: Number(b.ageLower),
              ageUpper: Number(b.ageUpper),
            }))
          : [],
      ],
      ...scalarInputs.map((input): [string, unknown] => {
        const raw = draftScalars[input.key] ?? "";
        const visible = input.showForTypes.includes(draftType);
        return [input.key, visible && raw !== "" ? Number(raw) : null];
      }),
    ];

    lastValueRef.current = draftType || null;
    if (initialCustomText) onCustomTextChange?.(null);

    const columnField = colDef?.field;
    if (columnField && node?.data) {
      writeCollapsedSiblings(
        node as { data: Record<string, unknown> },
        columnField,
        entries,
      );
    }

    stopEditing(true);

    setTimeout(() => {
      persistEntries(entries, typeKey, props);
      if (node) api.refreshCells({ rowNodes: [node], force: true });
    }, 0);
  };

  const handleCancel = (): void => {
    lastValueRef.current = initialType ?? null;
    stopEditing(true);
  };

  return {
    draftType,
    changeType,
    draftScalars,
    setScalar,
    bands,
    updateBand,
    addBand,
    removeBand,
    errors,
    showBandErrors,
    draftCustomText: customText.draftCustomText,
    setDraftCustomText: customText.setDraftCustomText,
    draftCharacterCount: customText.draftCharacterCount,
    setDraftCharacterCount: customText.setDraftCharacterCount,
    editMode: customText.editMode,
    setEditMode: customText.setEditMode,
    showBands,
    visibleScalars,
    customTextSupported: customText.customTextSupported,
    editingCustomText: customText.editingCustomText,
    getValue: (): unknown => lastValueRef.current,
    handleSave,
    handleCancel,
  };
}
